import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Calendar, MapPin, Users, FileText, ClipboardCheck, BedDouble, Bus,
  RefreshCcw, Mail, ArrowRight, CheckCircle2, X, Play,
} from "lucide-react";

const HONORIFICS = ["Mr", "Mrs", "Ms", "Dr", "Prof", "Other"];
const PRESENTATION_TYPES = ["Standard", "Speed", "Poster"];
const DIETARY = ["None", "Vegan", "Vegetarian", "Halaal", "Other"];

const SECTIONS = [
  { id: "about", label: "About" },
  { id: "dates", label: "Programme" },
  { id: "accepted", label: "Accepted Abstracts", link: "/conferences/symposium-2026/programme" },
  { id: "fees", label: "Fees" },
  { id: "abstracts", label: "Submit Abstract" },
  { id: "register", label: "Register" },
  { id: "accommodation", label: "Accommodation" },
  { id: "refunds", label: "Refunds" },
  { id: "contact", label: "Contact" },
];

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

export default function SymposiumPage() {
  const [content, setContent] = useState(null);
  const [myReg, setMyReg] = useState(null);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    api.get("/symposium-content").then(({ data }) => setContent(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isAuthenticated) { setMyReg(null); return; }
    api.get("/my-symposium-registration").then(({ data }) => setMyReg(data)).catch(() => {});
  }, [isAuthenticated]);

  if (!content) return <div className="ser-section text-center">Loading…</div>;

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="symposium-page" style={{ backgroundColor: "#fff" }}>
      <SymposiumHero content={content} onJump={scrollTo} />
      <SymposiumNav onJump={scrollTo} />
      <AboutSection content={content} />
      <RecordingSection />
      <ProgrammeSection content={content} />
      <FeesSection content={content} />
      <AbstractSection content={content} />
      <RegisterSection content={content} myReg={myReg} onRegistered={setMyReg} />
      <AccommodationSection content={content} />
      <RefundsSection content={content} />
      <PartnersSection content={content} />
      <ContactSection content={content} />
    </div>
  );
}

/* ── Hero ── */
function SymposiumHero({ content, onJump }) {
  return (
    <section className="relative h-[460px] md:h-[520px] overflow-hidden" data-testid="symposium-hero">
      <img src={content.hero_image} alt={content.name} className="absolute inset-0 w-full h-full object-cover" />
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(120deg, rgba(0,43,65,0.82) 0%, rgba(0,43,65,0.55) 55%, rgba(0,43,65,0.25) 100%)" }}
      />
      <div className="relative ser-container h-full flex flex-col justify-center px-6 md:px-12 text-white ser-fade-up">
        <p className="eyebrow mb-4" style={{ color: "#fff", opacity: 0.85 }}>SER Africa Symposium</p>
        <h1 className="font-serif-display text-3xl md:text-5xl lg:text-6xl leading-[1.15] max-w-3xl mb-4" style={{ fontWeight: 300 }}>
          {content.name}
        </h1>
        <p className="text-base md:text-lg max-w-2xl mb-6" style={{ color: "rgba(255,255,255,0.92)" }}>
          {content.tagline}
        </p>
        <div className="flex flex-wrap gap-4 text-sm mb-6" style={{ color: "rgba(255,255,255,0.95)" }}>
          <span className="flex items-center gap-2"><Calendar className="w-4 h-4" /> {content.dates}</span>
          <span className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {content.venue_name}, {content.venue_location}</span>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => onJump("register")} className="btn-ser-primary" data-testid="hero-register-btn">
            Register Now
          </button>
          <button
            onClick={() => onJump("abstracts")}
            className="btn-ser-outline"
            style={{ borderColor: "#fff", color: "#fff" }}
            data-testid="hero-abstract-btn"
          >
            Submit an Abstract
          </button>
        </div>
      </div>
    </section>
  );
}

/* ── Sticky in-page nav ── */
function SymposiumNav({ onJump }) {
  return (
    <div
      className="sticky top-20 z-30 border-b backdrop-blur-md"
      style={{ backgroundColor: "rgba(255,255,255,0.92)", borderColor: "var(--ser-border)" }}
      data-testid="symposium-nav"
    >
      <div className="ser-container px-6 md:px-12 py-3 overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {SECTIONS.map((s) => (
            s.link ? (
              <Link
                key={s.id}
                to={s.link}
                className="px-4 py-2 text-xs font-bold uppercase tracking-[0.1em] rounded-sm hover:bg-[var(--ser-section-alt)]"
                style={{ color: "var(--ser-primary)", fontFamily: "Lato, sans-serif" }}
                data-testid={`symposium-nav-${s.id}`}
              >
                {s.label}
              </Link>
            ) : (
              <button
                key={s.id}
                onClick={() => onJump(s.id)}
                className="px-4 py-2 text-xs font-bold uppercase tracking-[0.1em] rounded-sm hover:bg-[var(--ser-section-alt)]"
                style={{ color: "var(--ser-teal)", fontFamily: "Lato, sans-serif" }}
                data-testid={`symposium-nav-${s.id}`}
              >
                {s.label}
              </button>
            )
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── About ── */
function AboutSection({ content }) {
  return (
    <section id="about" className="ser-section" data-testid="about-section">
      <div className="ser-container max-w-4xl">
        <p className="eyebrow mb-3">About the Symposium</p>
        <h2 className="font-serif-display text-2xl md:text-3xl uppercase mb-6"
          style={{ color: "var(--ser-teal)", fontWeight: 300, letterSpacing: "0.04em" }}>
          A continental gathering for restoration
        </h2>
        <p className="leading-relaxed text-[0.97rem]" style={{ color: "var(--ser-text-muted)" }}>
          {content.about_body}
        </p>
      </div>
    </section>
  );
}

/* ── Watch the Recording ── */
function RecordingSection() {
  return (
    <section className="ser-section" data-testid="recording-section">
      <div className="ser-container max-w-4xl">
        <div className="flex items-center gap-2 mb-3">
          <Play className="w-4 h-4" style={{ color: "var(--ser-clay)" }} />
          <p className="eyebrow mb-0">Watch the Recording</p>
        </div>
        <h2 className="font-serif-display text-2xl md:text-3xl uppercase mb-6"
          style={{ color: "var(--ser-teal)", fontWeight: 300, letterSpacing: "0.04em" }}>
          Symposium Highlights
        </h2>

        <div className="relative w-full rounded overflow-hidden mb-6" style={{ paddingBottom: "56.25%" }}>
          <iframe
            className="absolute inset-0 w-full h-full"
            src="https://www.youtube.com/embed/rTp2N_PZbeY"
            title="Africa Ecosystem Restoration Symposium 2026 Recording"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        <div className="space-y-4 text-[0.97rem] leading-relaxed" style={{ color: "var(--ser-text-muted)" }}>
          <p>
            Have questions about the Symposium, the programme, speakers, participation or what to expect?
            We'd love to hear from you. Please leave your questions in the comments or contact the SER Africa team for assistance.
          </p>
          <p>
            We look forward to bringing together restoration practitioners, researchers, students, and partners
            from across the continent to advance ecosystem restoration in Africa.
          </p>
          <p className="font-semibold" style={{ color: "var(--ser-primary-dark)" }}>
            SER Africa — Restoring Africa Together.
          </p>
          <div className="flex flex-wrap gap-2 pt-2">
            {["#SERAfrica", "#AfricaEcosystemRestorationSymposium", "#EcologicalRestoration", "#EcosystemRestoration", "#RestorationEcology"].map((tag) => (
              <span key={tag} className="text-xs px-2.5 py-1 rounded-sm" style={{ backgroundColor: "var(--ser-section-alt)", color: "var(--ser-teal)", fontWeight: 600 }}>
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Programme / Dates ── */
function ProgrammeSection({ content }) {
  return (
    <section id="dates" className="ser-section" style={{ backgroundColor: "var(--ser-section-alt)" }} data-testid="programme-section">
      <div className="ser-container max-w-4xl">
        <p className="eyebrow mb-3">Programme & Key Dates</p>
        <h2 className="font-serif-display text-2xl md:text-3xl uppercase mb-6"
          style={{ color: "var(--ser-teal)", fontWeight: 300, letterSpacing: "0.04em" }}>
          When and where
        </h2>
        <div className="grid md:grid-cols-2 gap-5 mb-8">
          <div className="ser-card p-6">
            <div className="flex items-center gap-3 mb-3">
              <Calendar className="w-5 h-5" style={{ color: "var(--ser-primary)" }} />
              <p className="text-sm uppercase font-bold tracking-wider" style={{ color: "var(--ser-teal)" }}>Dates</p>
            </div>
            <p style={{ color: "var(--ser-text-muted)" }}>{content.dates}</p>
          </div>
          <div className="ser-card p-6">
            <div className="flex items-center gap-3 mb-3">
              <MapPin className="w-5 h-5" style={{ color: "var(--ser-primary)" }} />
              <p className="text-sm uppercase font-bold tracking-wider" style={{ color: "var(--ser-teal)" }}>Venue</p>
            </div>
            <p style={{ color: "var(--ser-text-muted)" }}>
              <strong>{content.venue_name}</strong><br />{content.venue_location}
            </p>
          </div>
        </div>
        <div className="ser-card p-6">
          <h3 className="text-sm uppercase font-bold tracking-wider mb-3" style={{ color: "var(--ser-teal)" }}>Detailed programme</h3>
          <p style={{ color: "var(--ser-text-muted)" }}>{content.programme_body}</p>
          <a
            href="/api/media/ser-africa/uploads/AERS_2026_Draft_Programme-a84fcf07.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 text-xs font-bold uppercase tracking-wider rounded"
            style={{ backgroundColor: "var(--ser-primary)", color: "white" }}
            data-testid="download-programme-pdf"
          >
            <FileText className="w-4 h-4" />
            Download Draft Programme (PDF)
          </a>
        </div>
        <div className="ser-card p-6 mt-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          style={{ backgroundColor: "var(--ser-primary-dark)", color: "white", borderLeft: "4px solid var(--ser-clay)" }}
          data-testid="programme-cta">
          <div>
            <p className="eyebrow mb-1" style={{ color: "var(--ser-clay)" }}>Accepted abstracts</p>
            <h3 className="font-serif-display text-xl" style={{ fontWeight: 300 }}>
              Browse the accepted programme
            </h3>
            <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.75)" }}>
              Titles, presenters and abstracts of every talk and poster the Scientific Committee has accepted.
            </p>
          </div>
          <Link
            to="/conferences/symposium-2026/programme"
            className="inline-flex items-center gap-2 px-6 py-3 text-xs font-bold uppercase tracking-[0.1em] shrink-0"
            style={{ backgroundColor: "var(--ser-clay)", color: "white" }}
            data-testid="view-accepted-abstracts-btn"
          >
            View accepted abstracts <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ── Fees ── */
function FeesSection({ content }) {
  return (
    <section id="fees" className="ser-section" data-testid="fees-section">
      <div className="ser-container max-w-5xl">
        <p className="eyebrow mb-3">Registration Fees</p>
        <h2 className="font-serif-display text-2xl md:text-3xl uppercase mb-8"
          style={{ color: "var(--ser-teal)", fontWeight: 300, letterSpacing: "0.04em" }}>
          Fees (in South African Rand)
        </h2>
        <div className="grid md:grid-cols-3 gap-5 mb-8">
          {content.fees.map((f) => (
            <div key={f.tier} className="ser-card p-6" data-testid={`fee-tier-${f.tier}`}>
              <p className="text-xs uppercase font-bold tracking-wider mb-1" style={{ color: "var(--ser-primary)" }}>
                {f.label}
              </p>
              <p className="text-sm mb-5" style={{ color: "var(--ser-text-muted)" }}>{f.period}</p>
              <ul className="space-y-2 text-sm">
                <li className="flex justify-between border-b pb-2" style={{ borderColor: "var(--ser-border)" }}>
                  <span style={{ color: "var(--ser-text-muted)" }}>SER members</span>
                  <strong style={{ color: "var(--ser-teal)" }}>ZAR {f.ser.toLocaleString("en-ZA")}</strong>
                </li>
                <li className="flex justify-between border-b pb-2" style={{ borderColor: "var(--ser-border)" }}>
                  <span style={{ color: "var(--ser-text-muted)" }}>Non-SER members</span>
                  <strong style={{ color: "var(--ser-teal)" }}>ZAR {f.non_ser.toLocaleString("en-ZA")}</strong>
                </li>
                <li className="flex justify-between">
                  <span style={{ color: "var(--ser-text-muted)" }}>Students</span>
                  <strong style={{ color: "var(--ser-teal)" }}>ZAR {f.student.toLocaleString("en-ZA")}</strong>
                </li>
              </ul>
            </div>
          ))}
        </div>
        <div className="ser-card p-5" style={{ borderLeft: "4px solid var(--ser-primary)" }}>
          <p className="text-sm leading-relaxed" style={{ color: "var(--ser-text-muted)" }}>
            <strong style={{ color: "var(--ser-teal)" }}>Payment:</strong> {content.payment_instructions}
          </p>
          <p className="text-sm leading-relaxed mt-2" style={{ color: "var(--ser-text-muted)" }}>
            {content.banking_details}
          </p>
        </div>
      </div>
    </section>
  );
}

/* ── Abstract submission ── */
function AbstractSection({ content }) {
  const empty = {
    title: "Mr", name: "", surname: "", email: "", institution: "",
    presentation_type: "Standard", co_authors: "", keywords: "",
    abstract_title: "", abstract_body: "", is_student: false,
  };
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const wordCount = useMemo(
    () => form.abstract_body.trim().split(/\s+/).filter(Boolean).length,
    [form.abstract_body]
  );
  const kwCount = useMemo(
    () => form.keywords.split(",").map((k) => k.trim()).filter(Boolean).length,
    [form.keywords]
  );

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (wordCount > 250) {
      toast.error(`Abstract is ${wordCount} words. Maximum is 250.`);
      return;
    }
    if (kwCount < 3) {
      toast.error("Please provide three keywords, separated by commas.");
      return;
    }
    if (!form.abstract_title.trim() || form.abstract_title.trim().length < 3) {
      toast.error("Please provide a title for your abstract.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/symposium/abstracts", form);
      toast.success("Abstract submitted successfully!");
      setSuccess(data);
      setForm(empty);
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setSubmitting(false); }
  };

  return (
    <section id="abstracts" className="ser-section" style={{ backgroundColor: "var(--ser-section-alt)" }} data-testid="abstract-section">
      <div className="ser-container max-w-3xl">
        <p className="eyebrow mb-3">Call for Abstracts</p>
        <h2 className="font-serif-display text-2xl md:text-3xl uppercase mb-6"
          style={{ color: "var(--ser-teal)", fontWeight: 300, letterSpacing: "0.04em" }}>
          {content.abstracts_closed ? "Submission closed" : "Submit your abstract"}
        </h2>

        {content.abstracts_closed ? (
          <div className="ser-card p-8" data-testid="abstracts-closed-card"
            style={{ borderLeft: "4px solid var(--ser-primary)" }}>
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="w-5 h-5" style={{ color: "var(--ser-primary)" }} />
              <p className="text-sm uppercase font-bold tracking-wider" style={{ color: "var(--ser-teal)" }}>
                Thank you — submissions are closed
              </p>
            </div>
            {(content.abstracts_closed_message || "").split(/\n{2,}/).map((para, i) => (
              <p key={i} className="text-sm leading-relaxed mb-3 whitespace-pre-line"
                style={{ color: "var(--ser-text-muted)" }}>
                {para}
              </p>
            ))}
            <div className="flex flex-wrap gap-3 mt-6">
              <Link to="/conferences/symposium-2026/programme"
                className="btn-ser-primary" data-testid="closed-view-accepted-btn">
                View the Accepted Abstracts <ArrowRight className="w-4 h-4 ml-1 inline" />
              </Link>
              <button type="button" onClick={() => {
                const el = document.getElementById("dates"); if (el) el.scrollIntoView({ behavior: "smooth" });
              }} className="btn-ser-outline" data-testid="closed-view-programme-btn">
                View the Symposium Programme
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="ser-card p-6 mb-6" style={{ borderLeft: "4px solid var(--ser-primary)" }}>
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4" style={{ color: "var(--ser-primary)" }} />
                <p className="text-sm uppercase font-bold tracking-wider" style={{ color: "var(--ser-teal)" }}>
                  Deadline: {formatDate(content.abstract_deadline)}
                </p>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: "var(--ser-text-muted)" }}>
                {content.abstract_guidelines}
              </p>
            </div>

            {success ? (
          <div className="ser-card p-8 text-center" data-testid="abstract-success">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-4" style={{ color: "var(--ser-primary)" }} />
            <h3 className="font-serif-display text-2xl mb-2" style={{ color: "var(--ser-teal)" }}>Abstract received</h3>
            <p style={{ color: "var(--ser-text-muted)" }}>{success.message}</p>
            <p className="text-xs mt-3" style={{ color: "var(--ser-text-muted)" }}>Reference: <code>{success.abstract_id}</code></p>
            <button onClick={() => setSuccess(null)} className="btn-ser-outline mt-6" data-testid="submit-another-abstract">
              Submit another abstract
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="ser-card p-6 space-y-4" data-testid="abstract-form">
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="ser-label">Title</label>
                <select className="ser-input" value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="abs-title">
                  {HONORIFICS.map((h) => <option key={h}>{h}</option>)}
                </select>
              </div>
              <div>
                <label className="ser-label">First name</label>
                <input required className="ser-input" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="abs-name" />
              </div>
              <div>
                <label className="ser-label">Surname</label>
                <input required className="ser-input" value={form.surname} onChange={(e) => set("surname", e.target.value)} data-testid="abs-surname" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="ser-label">Email</label>
                <input required type="email" className="ser-input" value={form.email} onChange={(e) => set("email", e.target.value.toLowerCase())} data-testid="abs-email" />
              </div>
              <div>
                <label className="ser-label">Institution</label>
                <input required className="ser-input" value={form.institution} onChange={(e) => set("institution", e.target.value)} data-testid="abs-institution" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="ser-label">Type of presentation</label>
                <select className="ser-input" value={form.presentation_type} onChange={(e) => set("presentation_type", e.target.value)} data-testid="abs-type">
                  {PRESENTATION_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
                <p className="text-xs mt-1" style={{ color: "var(--ser-text-muted)" }}>
                  Standard 15 min · Speed 5 min · Poster
                </p>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.is_student}
                    onChange={(e) => set("is_student", e.target.checked)} data-testid="abs-student" />
                  <span style={{ color: "var(--ser-text-muted)" }}>I am a student presenter</span>
                </label>
              </div>
            </div>
            <div>
              <label className="ser-label">Co-authors and their institutions</label>
              <textarea rows={2} className="ser-input" value={form.co_authors} onChange={(e) => set("co_authors", e.target.value)}
                placeholder="e.g. Jane Doe (Stellenbosch University); John Smith (SANBI)"
                data-testid="abs-coauthors" />
            </div>
            <div>
              <label className="ser-label">Three keywords <span className="font-normal lowercase" style={{ color: "var(--ser-text-muted)" }}>(comma-separated)</span></label>
              <input className="ser-input" value={form.keywords} onChange={(e) => set("keywords", e.target.value)}
                placeholder="e.g. grassland, restoration, biodiversity" data-testid="abs-keywords" />
              <p className="text-xs mt-1" style={{ color: kwCount >= 3 ? "var(--ser-primary)" : "var(--ser-text-muted)" }}>
                {kwCount} / 3 keywords
              </p>
            </div>
            <div>
              <label className="ser-label">Title <span className="font-normal lowercase" style={{ color: "var(--ser-text-muted)" }}>(of your abstract)</span></label>
              <input required maxLength={200} className="ser-input" value={form.abstract_title}
                onChange={(e) => set("abstract_title", e.target.value)}
                placeholder="e.g. Community-led grassland restoration in the KwaZulu-Natal midlands"
                data-testid="abs-abstract-title" />
            </div>
            <div>
              <label className="ser-label">Abstract <span className="font-normal lowercase" style={{ color: "var(--ser-text-muted)" }}>(max 250 words)</span></label>
              <textarea required rows={8} className="ser-input" value={form.abstract_body}
                onChange={(e) => set("abstract_body", e.target.value)}
                placeholder="Describe the purpose, methods, results, and conclusions of the work."
                data-testid="abs-body" />
              <p className="text-xs mt-1" style={{ color: wordCount > 250 ? "var(--destructive)" : "var(--ser-text-muted)" }}>
                {wordCount} / 250 words
              </p>
            </div>
            <button type="submit" disabled={submitting} className="btn-ser-primary w-full sm:w-auto" data-testid="submit-abstract-btn">
              {submitting ? "Submitting…" : "Submit abstract"}
            </button>
          </form>
        )}
          </>
        )}
      </div>
    </section>
  );
}

/* ── Registration ── */
function RegisterSection({ content, myReg, onRegistered }) {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const empty = {
    title: "Mr", name: user?.name?.split(" ")[0] || "", surname: user?.name?.split(" ").slice(1).join(" ") || "",
    email: user?.email || "", mobile: user?.cell_number || "", institution: "",
    abstract_submitted: false, dietary: "None", dietary_other: "",
    ser_membership: "SER member", is_student: false, shuttle_needed: false, invoice_address: "",
  };
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  // Compute estimated fee from today's tier (display only — server is authoritative)
  const todayFee = useMemo(() => {
    const today = new Date();
    const tier = (content.fees || []).find((f) => {
      const start = f.start ? new Date(f.start) : null;
      const end = f.end ? new Date(f.end) : null;
      return (!start || today >= start) && (!end || today <= end);
    }) || (content.fees || []).slice(-1)[0];
    if (!tier) return null;
    const amt = form.is_student ? tier.student
      : form.ser_membership === "SER member" ? tier.ser : tier.non_ser;
    return { tier, amount: amt };
  }, [content.fees, form.is_student, form.ser_membership]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.info("Please sign in to register for the Symposium.");
      navigate("/login", { state: { from: "/conferences/symposium-2026" } });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/symposium/registrations", form);
      toast.success("Registration received!");
      onRegistered(data.registration);
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setSubmitting(false); }
  };

  if (myReg) {
    return (
      <section id="register" className="ser-section" data-testid="register-section">
        <div className="ser-container max-w-3xl">
          <p className="eyebrow mb-3">Your Registration</p>
          <h2 className="font-serif-display text-2xl md:text-3xl uppercase mb-6"
            style={{ color: "var(--ser-teal)", fontWeight: 300, letterSpacing: "0.04em" }}>
            You're registered
          </h2>
          <div className="ser-card p-6" data-testid="my-registration-card">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-5 h-5" style={{ color: "var(--ser-primary)" }} />
              <span className="text-sm uppercase font-bold tracking-wider" style={{ color: "var(--ser-primary)" }}>
                {myReg.payment_status === "paid" ? "Paid" : "Payment pending"}
              </span>
            </div>
            <h3 className="font-serif-display text-xl mb-2" style={{ color: "var(--ser-teal)" }}>
              {myReg.title} {myReg.name} {myReg.surname}
            </h3>
            <p className="text-sm mb-4" style={{ color: "var(--ser-text-muted)" }}>
              {myReg.institution} · {myReg.email}
            </p>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div><strong>Reference:</strong> <code>{myReg.registration_id}</code></div>
              <div><strong>Tier:</strong> {myReg.fee_tier_label}</div>
              <div><strong>Amount due:</strong> ZAR {Number(myReg.fee_amount_zar).toLocaleString("en-ZA")}</div>
              <div><strong>SER status:</strong> {myReg.ser_membership}</div>
              <div><strong>Student:</strong> {myReg.is_student ? "Yes" : "No"}</div>
              <div><strong>Shuttle:</strong> {myReg.shuttle_needed ? "Yes" : "No"}</div>
              <div><strong>Dietary:</strong> {myReg.dietary === "Other" ? `Other: ${myReg.dietary_other}` : myReg.dietary}</div>
              <div><strong>Abstract submitted:</strong> {myReg.abstract_submitted ? "Yes" : "No"}</div>
            </div>
            <p className="mt-5 text-sm leading-relaxed" style={{ color: "var(--ser-text-muted)" }}>
              {content.payment_instructions}
            </p>
            <p className="mt-2 text-sm" style={{ color: "var(--ser-text-muted)" }}>
              For changes or cancellations contact <a href="mailto:africa@ser.org" className="underline" style={{ color: "var(--ser-primary)" }}>africa@ser.org</a>.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="register" className="ser-section" data-testid="register-section">
      <div className="ser-container max-w-3xl">
        <p className="eyebrow mb-3">Reserve your spot</p>
        <h2 className="font-serif-display text-2xl md:text-3xl uppercase mb-6"
          style={{ color: "var(--ser-teal)", fontWeight: 300, letterSpacing: "0.04em" }}>
          Symposium registration
        </h2>

        {!isAuthenticated && (
          <div className="ser-card p-5 mb-6" style={{ borderLeft: "4px solid var(--ser-clay)" }}>
            <p className="text-sm" style={{ color: "var(--ser-text-muted)" }}>
              You'll need to <Link to="/login" className="underline" style={{ color: "var(--ser-primary)" }}>sign in or create an account</Link> to complete your registration.
            </p>
          </div>
        )}

        {todayFee && (
          <div className="ser-card p-4 mb-5 flex flex-wrap items-center justify-between gap-3"
            style={{ backgroundColor: "var(--ser-section-alt)" }}>
            <div>
              <p className="text-xs uppercase font-bold tracking-wider" style={{ color: "var(--ser-primary)" }}>
                Current tier: {todayFee.tier.label}
              </p>
              <p className="text-xs" style={{ color: "var(--ser-text-muted)" }}>{todayFee.tier.period}</p>
            </div>
            <p className="text-xl font-bold" style={{ color: "var(--ser-teal)" }}>
              ZAR {todayFee.amount.toLocaleString("en-ZA")}
            </p>
          </div>
        )}

        <form onSubmit={submit} className="ser-card p-6 space-y-4" data-testid="register-form">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="ser-label">Title</label>
              <select className="ser-input" value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="reg-title">
                {HONORIFICS.map((h) => <option key={h}>{h}</option>)}
              </select>
            </div>
            <div>
              <label className="ser-label">First name</label>
              <input required className="ser-input" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="reg-name" />
            </div>
            <div>
              <label className="ser-label">Surname</label>
              <input required className="ser-input" value={form.surname} onChange={(e) => set("surname", e.target.value)} data-testid="reg-surname" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="ser-label">Email</label>
              <input required type="email" className="ser-input" value={form.email}
                onChange={(e) => set("email", e.target.value.toLowerCase())} data-testid="reg-email" />
            </div>
            <div>
              <label className="ser-label">Mobile (incl. country code)</label>
              <input required className="ser-input" placeholder="+27 …" value={form.mobile}
                onChange={(e) => set("mobile", e.target.value)} data-testid="reg-mobile" />
            </div>
          </div>
          <div>
            <label className="ser-label">Institution</label>
            <input required className="ser-input" value={form.institution} onChange={(e) => set("institution", e.target.value)} data-testid="reg-institution" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="ser-label">SER membership</label>
              <select className="ser-input" value={form.ser_membership}
                onChange={(e) => set("ser_membership", e.target.value)} data-testid="reg-membership">
                <option>SER member</option>
                <option>Non-SER member</option>
              </select>
            </div>
            <div>
              <label className="ser-label">Abstract submitted?</label>
              <select className="ser-input" value={form.abstract_submitted ? "Yes" : "No"}
                onChange={(e) => set("abstract_submitted", e.target.value === "Yes")} data-testid="reg-abstract">
                <option>No</option>
                <option>Yes</option>
              </select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="ser-label">Dietary requirements</label>
              <select className="ser-input" value={form.dietary}
                onChange={(e) => set("dietary", e.target.value)} data-testid="reg-dietary">
                {DIETARY.map((d) => <option key={d}>{d}</option>)}
              </select>
              {form.dietary === "Other" && (
                <input className="ser-input mt-2" placeholder="Please specify"
                  value={form.dietary_other} onChange={(e) => set("dietary_other", e.target.value)}
                  data-testid="reg-dietary-other" />
              )}
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_student}
                  onChange={(e) => set("is_student", e.target.checked)} data-testid="reg-student" />
                <span className="text-sm" style={{ color: "var(--ser-text-muted)" }}>I am a student</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.shuttle_needed}
                  onChange={(e) => set("shuttle_needed", e.target.checked)} data-testid="reg-shuttle" />
                <span className="text-sm" style={{ color: "var(--ser-text-muted)" }}>I need shuttle transport</span>
              </label>
            </div>
          </div>
          <div>
            <label className="ser-label">Address for invoice</label>
            <textarea required rows={3} className="ser-input" value={form.invoice_address}
              onChange={(e) => set("invoice_address", e.target.value)}
              placeholder="Street, City, Postal code, Country" data-testid="reg-invoice" />
          </div>
          <button type="submit" disabled={submitting} className="btn-ser-primary w-full sm:w-auto" data-testid="submit-registration-btn">
            {submitting ? "Submitting…" : "Complete registration"}
          </button>
        </form>
      </div>
    </section>
  );
}

/* ── Accommodation & Transport ── */
function AccommodationSection({ content }) {
  return (
    <section id="accommodation" className="ser-section" style={{ backgroundColor: "var(--ser-section-alt)" }} data-testid="accommodation-section">
      <div className="ser-container max-w-4xl grid md:grid-cols-2 gap-6">
        <div className="ser-card p-6">
          <div className="flex items-center gap-3 mb-3">
            <BedDouble className="w-5 h-5" style={{ color: "var(--ser-primary)" }} />
            <h3 className="text-sm uppercase font-bold tracking-wider" style={{ color: "var(--ser-teal)" }}>Accommodation</h3>
          </div>
          <p className="text-sm leading-relaxed mb-4" style={{ color: "var(--ser-text-muted)" }}>{content.accommodation_text}</p>
          <ul className="space-y-2">
            {content.accommodation_links.map((l) => (
              <li key={l.url}>
                <a href={l.url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm hover:underline"
                  style={{ color: "var(--ser-primary)" }}>
                  <ArrowRight className="w-3.5 h-3.5" /> {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div className="ser-card p-6">
          <div className="flex items-center gap-3 mb-3">
            <Bus className="w-5 h-5" style={{ color: "var(--ser-primary)" }} />
            <h3 className="text-sm uppercase font-bold tracking-wider" style={{ color: "var(--ser-teal)" }}>Transport & shuttle</h3>
          </div>
          <p className="text-sm leading-relaxed" style={{ color: "var(--ser-text-muted)" }}>{content.transport_text}</p>
        </div>
      </div>
    </section>
  );
}

/* ── Refund policy ── */
function RefundsSection({ content }) {
  return (
    <section id="refunds" className="ser-section" data-testid="refunds-section">
      <div className="ser-container max-w-4xl">
        <p className="eyebrow mb-3">Cancellation & Refund Policy</p>
        <h2 className="font-serif-display text-2xl md:text-3xl uppercase mb-6"
          style={{ color: "var(--ser-teal)", fontWeight: 300, letterSpacing: "0.04em" }}>
          If your plans change
        </h2>
        <div className="ser-card overflow-hidden">
          <table className="w-full text-sm">
            <thead style={{ backgroundColor: "var(--ser-section-alt)" }}>
              <tr style={{ color: "var(--ser-teal)" }}>
                <th className="p-4 text-left text-xs uppercase tracking-wider font-bold">Cancellation window</th>
                <th className="p-4 text-left text-xs uppercase tracking-wider font-bold">Refund</th>
              </tr>
            </thead>
            <tbody>
              {content.refund_policy.map((r) => (
                <tr key={r.window} className="border-t" style={{ borderColor: "var(--ser-border)" }}>
                  <td className="p-4" style={{ color: "var(--ser-text-muted)" }}>{r.window}</td>
                  <td className="p-4"><strong style={{ color: "var(--ser-teal)" }}>{r.refund}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs mt-4 leading-relaxed" style={{ color: "var(--ser-text-muted)" }}>{content.refund_note}</p>
      </div>
    </section>
  );
}

/* ── Partners ── */
function PartnersSection({ content }) {
  const partners = content.partners || [];
  if (partners.length === 0) return null;
  return (
    <section className="ser-section" data-testid="partners-section">
      <div className="ser-container max-w-5xl text-center">
        <p
          className="text-[0.72rem] tracking-[0.18em] uppercase font-bold mb-8"
          style={{ color: "var(--ser-text-muted)", fontFamily: "Lato, sans-serif" }}
        >
          {content.partners_heading || "Implemented in partnership with"}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-8">
          {partners.map((p, i) => {
            const key = p.logo_url || p.name || `partner-${i}`;
            const img = (
              <img
                src={p.logo_url}
                alt={p.name}
                title={p.name}
                className="max-h-32 md:max-h-36 w-auto object-contain transition-transform duration-300 hover:scale-105"
              />
            );
            return p.website ? (
              <a
                key={key}
                href={p.website}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`partner-${i}`}
                aria-label={p.name}
              >
                {img}
              </a>
            ) : (
              <div key={key} data-testid={`partner-${i}`}>{img}</div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ── Contact ── */
function ContactSection({ content }) {
  return (
    <section id="contact" className="ser-section" style={{ backgroundColor: "var(--ser-section-alt)" }} data-testid="symposium-contact-section">
      <div className="ser-container max-w-5xl">
        <p className="eyebrow mb-3">Contact</p>
        <h2 className="font-serif-display text-2xl md:text-3xl uppercase mb-8"
          style={{ color: "var(--ser-teal)", fontWeight: 300, letterSpacing: "0.04em" }}>
          Symposium Co-chairs
        </h2>
        <div className="grid sm:grid-cols-2 gap-5">
          {content.co_chairs.map((c) => (
            <div key={c.email} className="ser-card p-5" data-testid={`cochair-${c.email}`}>
              <p className="text-xs uppercase font-bold tracking-wider mb-1" style={{ color: "var(--ser-primary)" }}>
                {c.role}
              </p>
              <p className="font-serif-display text-lg mb-1" style={{ color: "var(--ser-teal)" }}>{c.name}</p>
              <a href={`mailto:${c.email}`} className="inline-flex items-center gap-2 text-sm hover:underline"
                style={{ color: "var(--ser-text-muted)" }}>
                <Mail className="w-3.5 h-3.5" /> {c.email}
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
